import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { useSubPrices, useUpdateSubPrices } from "@/lib/queries/subscriptions"

// ───────────────────────────────────────────────────────────────────────
// PAGE: Subscriptions (settings, single form — no list)
// LEGACY: apps/admin/src/pages/SubscriptionSetting.jsx
// API:    POST /admin/get_sub_price       (no body)
//         POST /admin/update_sub_price    { subscription_price_monthly, subscription_price_yearly }
//
// ─── Field parity (legacy ↔ v2) ──────────────────────────────────────
//   subscription_price_monthly  ← Monthly input (number)
//   subscription_price_yearly   ← Yearly input (number)
//
// Behavior parity:
//   - Mount fetches /get_sub_price; finds rows by months: 1 (monthly) and 12 (yearly)
//   - Save POSTs both fields together (legacy did the same)
//   - Toast on success / failure (v2 uses inline alert + auto-clear)
// ───────────────────────────────────────────────────────────────────────

const schema = z.object({
  subscription_price_monthly: z.coerce.number().min(0),
  subscription_price_yearly: z.coerce.number().min(0),
})

type FormValues = z.infer<typeof schema>

export default function Subscriptions() {
  const pricesQ = useSubPrices()
  const updateMut = useUpdateSubPrices()

  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      subscription_price_monthly: 0,
      subscription_price_yearly: 0,
    },
  })

  // Hydrate when prices load
  useEffect(() => {
    if (!pricesQ.data) return
    reset({
      subscription_price_monthly: pricesQ.data.monthly,
      subscription_price_yearly: pricesQ.data.yearly,
    })
  }, [pricesQ.data, reset])

  // Auto-clear the success banner after a few seconds
  useEffect(() => {
    if (!submitSuccess) return
    const t = setTimeout(() => setSubmitSuccess(null), 4000)
    return () => clearTimeout(t)
  }, [submitSuccess])

  const onSubmit = handleSubmit(async (values) => {
    setSubmitError(null)
    setSubmitSuccess(null)
    try {
      const res = await updateMut.mutateAsync(values)
      if (!res?.success) {
        setSubmitError(res?.message || "Failed to update subscription prices.")
        return
      }
      setSubmitSuccess("Subscription prices updated.")
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : "Failed to update subscription prices."
      )
    }
  })

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Subscription pricing</CardTitle>
          <CardDescription>
            Prices shown to mobile users in the upgrade flow. Saved values
            apply to new subscriptions immediately.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {pricesQ.isError && (
            <div
              role="alert"
              className="mb-4 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>Failed to load current prices. Try refreshing.</span>
            </div>
          )}

          {pricesQ.isLoading ? (
            <div className="grid h-32 place-items-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : (
            <form className="space-y-6" onSubmit={onSubmit} noValidate>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="monthly">Monthly price</Label>
                  <div className="relative">
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                      USD
                    </span>
                    <Input
                      id="monthly"
                      type="number"
                      min={0}
                      step="0.01"
                      className="pr-12"
                      aria-invalid={!!errors.subscription_price_monthly}
                      {...register("subscription_price_monthly")}
                    />
                  </div>
                  {errors.subscription_price_monthly && (
                    <p className="text-xs text-destructive">
                      {errors.subscription_price_monthly.message}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="yearly">Yearly price</Label>
                  <div className="relative">
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                      USD
                    </span>
                    <Input
                      id="yearly"
                      type="number"
                      min={0}
                      step="0.01"
                      className="pr-12"
                      aria-invalid={!!errors.subscription_price_yearly}
                      {...register("subscription_price_yearly")}
                    />
                  </div>
                  {errors.subscription_price_yearly && (
                    <p className="text-xs text-destructive">
                      {errors.subscription_price_yearly.message}
                    </p>
                  )}
                </div>
              </div>

              {submitError && (
                <div
                  role="alert"
                  className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
                >
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{submitError}</span>
                </div>
              )}

              {submitSuccess && (
                <div
                  role="status"
                  className="flex items-start gap-2 rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-700"
                >
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{submitSuccess}</span>
                </div>
              )}

              <div className="flex items-center gap-2">
                <Button type="submit" disabled={updateMut.isPending}>
                  {updateMut.isPending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Update prices
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
